import {useCallback, useEffect, useState} from "react";
import {useLazyQuery, useMutation} from "@apollo/client";
import {trackPromise, usePromiseTracker} from "react-promise-tracker";
import {FormProvider, useForm} from "react-hook-form";
import {showToast} from "common";
import {LoadingSpinner} from "common";
import {FatOutlineButton} from "common/component/FatOutlineButton";
import {ModalTextAreaInput} from "../form-components/ModalTextAreaInput";
import {ReportModal} from "../form-components/ReportModal";
import {toRelayId} from "../../../graphqlsupport";
import {
    DUAL_PROVIDER_AVAILABLE_REPORTS_QUERY,
    LATEST_CARE_COORDINATION_REQUEST_QUERY,
    SUBMIT_CARE_COORDINATION_REQUEST_MUTATION,
    DualProviderAvailableReportsResult,
    LatestCareCoordinationRequestResult,
    AvailableReport
} from "./queries";

type CareCoordinationFormValues = {
    additionalNotes: string;
};

type CareCoordinationRequestModalProps = {
    visible: boolean;
    onClose: () => void;
    clientGuid: string;
    otherProviderName: string | null;
    onSubmitSuccess?: () => void | Promise<void>;
};

function formatDate(dateStr: string): string {
    try {
        const date = new Date(dateStr);
        return date.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
        });
    } catch {
        return dateStr;
    }
}

export function CareCoordinationRequestModal({
    visible,
    onClose,
    clientGuid,
    otherProviderName,
    onSubmitSuccess,
}: CareCoordinationRequestModalProps) {
    const methods = useForm<CareCoordinationFormValues>({
        defaultValues: {additionalNotes: ""},
    });

    const [fetchReports, {data: reportsData, loading: reportsLoading, error: reportsError}] =
        useLazyQuery<DualProviderAvailableReportsResult>(DUAL_PROVIDER_AVAILABLE_REPORTS_QUERY, {
            fetchPolicy: "network-only",
        });
    const [fetchLatestRequest, {data: latestRequestData, loading: latestRequestLoading, error: latestRequestError}] =
        useLazyQuery<LatestCareCoordinationRequestResult>(LATEST_CARE_COORDINATION_REQUEST_QUERY, {
            fetchPolicy: "network-only",
        });

    const [submitMutation] = useMutation(SUBMIT_CARE_COORDINATION_REQUEST_MUTATION);

    const {promiseInProgress} = usePromiseTracker();

    const [hasFetched, setHasFetched] = useState(false);

    useEffect(() => {
        if (visible && clientGuid && !hasFetched) {
            fetchReports({variables: {clientGuid}});
            fetchLatestRequest({variables: {clientProfileGuid: toRelayId(clientGuid, "ClientProfile")}});
            setHasFetched(true);
        }
        if (!visible) {
            setHasFetched(false);
        }
    }, [visible, clientGuid, hasFetched, fetchReports, fetchLatestRequest]);

    const availableReports: AvailableReport[] = reportsData?.dualProviderAvailableReports ?? [];
    const hasReports = availableReports.length > 0;

    const latestCareCoordinationRequest = latestRequestData?.clientProfileQuery?.latestCareCoordinationRequest;
    const doneStatuses = ["Closed", "Resolved", "Completed"];
    const hasPendingRequest = latestCareCoordinationRequest != null && !doneStatuses.includes(latestCareCoordinationRequest.status);

    const handleSubmit = useCallback(
        async (formValues: CareCoordinationFormValues, reportGuids: string[]) => {
            try {
                await trackPromise(
                    submitMutation({
                        variables: {
                            clientGuid,
                            additionalNotes: formValues.additionalNotes || null,
                            reportGuids,
                        },
                    })
                );
                showToast.success("Care coordination request submitted successfully.");
                methods.reset();
                await onSubmitSuccess?.();
                onClose();
            } catch (err: any) {
                const gqlError = err?.graphQLErrors?.[0];
                if (gqlError?.extensions?.errorCode === "DUPLICATE_CARE_COORDINATION_REQUEST") {
                    showToast.error(gqlError.message || "A care coordination request is already in progress.");
                } else if (gqlError?.extensions?.errorCode === "STALE_AVAILABLE_REPORTS") {
                    showToast.error(
                        gqlError.message ||
                            "The available reports have changed. Please refresh the page to get the latest reports to share."
                    );
                } else {
                    showToast.error(
                        gqlError?.message || "Failed to submit care coordination request. Please try again."
                    );
                }
            }
        },
        [submitMutation, clientGuid, methods, onSubmitSuccess, onClose]
    );

    const handleCancel = useCallback(() => {
        methods.reset();
        onClose();
    }, [methods, onClose]);

    const isLoading = reportsLoading || latestRequestLoading || promiseInProgress;
    const loadError = reportsError || latestRequestError;

    return (
        <ReportModal
            visible={visible}
            title="Request Care Coordination"
            maxWidth="max-w-3xl"
            onClose={handleCancel}
            closeDisabled={promiseInProgress}
        >
            {isLoading && (
                <div className="p-24">
                    <LoadingSpinner text={promiseInProgress ? "Processing" : "Loading"} />
                </div>
            )}

            {!isLoading && loadError && (
                <div className="p-6">
                    <p className="text-red-600 text-sm">
                        Failed to load available reports. Please try again later.
                    </p>
                    <div className="flex justify-end mt-4">
                        <div className="w-32">
                            <FatOutlineButton color="red" text="Close" onClick={handleCancel} />
                        </div>
                    </div>
                </div>
            )}

            {!isLoading && !loadError && hasPendingRequest && (
                <div className="p-6">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
                        <p className="text-yellow-800 text-sm font-medium">
                            A care coordination request is already in progress for this client.
                            Please wait until the current request is completed before submitting a new one.
                        </p>
                    </div>
                    <div className="flex justify-end">
                        <div className="w-32">
                            <FatOutlineButton color="red" text="Close" onClick={handleCancel} />
                        </div>
                    </div>
                </div>
            )}

            {!isLoading && !loadError && !hasPendingRequest && hasReports && (
                <div className="p-6">
                    <p className="text-sm text-gray-700 mb-4">
                        The following report(s) / actions will be provided to help you coordinate care with{" "}
                        <strong>{otherProviderName || "the other provider"}</strong>:
                    </p>
                    <div className="border border-gray-200 rounded-md p-4 mb-4 bg-gray-50">
                        <p className="text-sm font-semibold text-gray-800 mb-2">You will be receiving:</p>
                        <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                            {availableReports.map((report) => (
                                <li key={report.guid}>
                                    {report.reportType}
                                    {report.bhpnAuthorizationNumber
                                        ? ` — ${report.bhpnAuthorizationNumber}`
                                        : ""}
                                    {report.submittedAt ? ` — ${formatDate(report.submittedAt)}` : ""}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <p className="text-xs text-gray-500 mb-6">
                        On submit, the report(s) will be generated to your{" "}
                        <strong>Client Reports</strong> view and a <strong>Care Coordination</strong>{" "}
                        entry will be added to your <strong>Requests</strong> screen.
                    </p>
                    <div className="flex justify-end border-t pt-4">
                        <div className="flex w-56 gap-3">
                            <FatOutlineButton color="red" text="Cancel" onClick={handleCancel} />
                            <FatOutlineButton
                                color="green"
                                text="Submit"
                                onClick={() =>
                                    handleSubmit(
                                        {additionalNotes: ""},
                                        availableReports.map((report) => report.guid)
                                    )
                                }
                            />
                        </div>
                    </div>
                </div>
            )}

            {!isLoading && !loadError && !hasPendingRequest && !hasReports && (
                <FormProvider {...methods}>
                    <form autoComplete="off" onSubmit={methods.handleSubmit((values) => handleSubmit(values, []))}>
                        <div className="p-6">
                            <p className="text-sm text-gray-700 mb-4">
                                The following report(s) / actions are being provided to help coordinate care with{" "}
                                <strong>{otherProviderName || "the other provider"}</strong>:
                            </p>
                            <div className="border border-gray-200 rounded-md p-4 mb-4 bg-gray-50">
                                <p className="text-sm font-semibold text-gray-800 mb-2">You will be receiving:</p>
                                <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                                    <li>Most recently completed report from dual provider</li>
                                    <li>If the most recent report is an Addendum, includes the next most recent report</li>
                                </ul>
                            </div>

                            <ModalTextAreaInput<CareCoordinationFormValues>
                                label="Additional information (optional)"
                                disabled={promiseInProgress}
                                field="additionalNotes"
                                required={false}
                                rowsNum={4}
                                maxLength={4000}
                                labelPosition="top"
                                showCharacterCount
                                shouldStopInputOnMaxLength={false}
                            />

                            <div className="flex justify-end border-t pt-4 mt-4">
                                <div className="flex w-56 gap-3">
                                    <FatOutlineButton color="red" text="Cancel" onClick={handleCancel} />
                                    <FatOutlineButton text="Submit" color="green" type="submit" />
                                </div>
                            </div>
                        </div>
                    </form>
                </FormProvider>
            )}
        </ReportModal>
    );
}
